import React, { useState, useEffect, ChangeEvent, FormEvent, KeyboardEvent, useRef } from 'react';
import {
  Calendar,
  User,
  ArrowLeft,
  Tag,
  X,
  Save,
  Eye,
  AlertCircle,
  CheckCircle2,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import uploadImage from '../../helper/uploadImage';
import NavBar from '../Welcome/NavBar';
import { getIdToken } from 'firebase/auth';
import { auth } from '../../firebase.config';
import API from '../../api/api';
import { useNavigate } from 'react-router-dom';

// Types
interface BlogPost {
  id: number;
  title: string;
  excerpt: string;
  content: string;
  author: string;
  date: string;
  category: string;
  tags: string[];
  readTime: number;
  imageUrl?: string;
  views?: number;
  rating?: number;
  likes?: number;
  dislikes?: number;
  comments?: {
    id: string;
    author: string;
    text: string;
    date: string;
  }[];
}

interface BlogFormData {
  title: string;
  author: string;
  category: string;
  readTime: number;
  excerpt: string;
  content: string;
  tags: string[];
  imageUrl?: string;
}

interface FormErrors {
  title?: string;
  author?: string;
  category?: string;
  excerpt?: string;
  content?: string;
}

// Styles (CSS Modules approach)
const styles = {
  app: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    lineHeight: '1.6',
    color: '#333',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '0 20px'
  },
  header: {
    backgroundColor: '#1a1a1a',
    color: 'white',
    padding: '2rem 0',
    marginBottom: '2rem',
    borderRadius: '8px'
  },
  headerContent: {
    textAlign: 'center' as const
  },
  title: {
    fontSize: '2.5rem',
    marginBottom: '0.5rem',
    fontWeight: '700'
  },
  subtitle: {
    fontSize: '1.1rem',
    opacity: 0.9,
    fontWeight: '400'
  },
  searchContainer: {
    position: 'relative' as const,
    marginBottom: '2rem',
    maxWidth: '500px',
    margin: '0 auto 2rem'
  },
  searchInput: {
    width: '100%',
    padding: '12px 40px 12px 12px',
    fontSize: '16px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    outline: 'none',
    transition: 'border-color 0.3s ease'
  },
  searchIcon: {
    position: 'absolute' as const,
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#666'
  },
  filterContainer: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '2rem',
    flexWrap: 'wrap' as const,
    justifyContent: 'center'
  },
  filterBtn: {
    padding: '8px 16px',
    border: '1px solid #ddd',
    borderRadius: '20px',
    background: 'white',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '14px'
  },
  filterBtnActive: {
    backgroundColor: '#007bff',
    color: 'white',
    borderColor: '#007bff'
  },
  postsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '2rem',
    marginBottom: '3rem'
  },
  postCard: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    padding: '1.5rem',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    cursor: 'pointer',
    border: '1px solid #f0f0f0'
  },
  postCardHover: {
    transform: 'translateY(-4px)',
    boxShadow: '0 8px 15px rgba(0, 0, 0, 0.15)'
  },
  postTitle: {
    fontSize: '1.5rem',
    marginBottom: '0.5rem',
    fontWeight: '600',
    color: '#1a1a1a'
  },
  postExcerpt: {
    color: '#666',
    marginBottom: '1rem',
    lineHeight: '1.6'
  },
  postMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    fontSize: '0.875rem',
    color: '#888',
    marginBottom: '0.5rem'
  },
  postMetaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem'
  },
  postCategory: {
    display: 'inline-block',
    backgroundColor: '#f8f9fa',
    color: '#495057',
    padding: '0.25rem 0.75rem',
    borderRadius: '15px',
    fontSize: '0.75rem',
    fontWeight: '500',
    marginBottom: '1rem'
  },
  postTags: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem'
  },
  tag: {
    backgroundColor: '#e9ecef',
    color: '#495057',
    padding: '0.25rem 0.5rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem'
  },
  postDetail: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '2rem',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem'
  },
  backBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    backgroundColor: '#f8f9fa',
    border: 'none',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '0.875rem',
    color: '#495057',
    marginBottom: '1.5rem',
    transition: 'background-color 0.3s ease'
  },
  backBtnHover: {
    backgroundColor: '#e9ecef'
  },
  postDetailTitle: {
    fontSize: '2.5rem',
    marginBottom: '1rem',
    fontWeight: '700',
    color: '#1a1a1a',
    lineHeight: '1.2'
  },
  postDetailMeta: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '1.5rem',
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #eee'
  },
  postContent: {
    fontSize: '1.1rem',
    lineHeight: '1.8',
    color: '#333'
  },
  noResults: {
    textAlign: 'center' as const,
    padding: '3rem 1rem',
    color: '#666',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  // Form Styles
  formContainer: {
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: "5% 10%",
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    marginBottom: '2rem',
    border: '1px solid #f0f0f0'
  },
  formHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid #eee'
  },
  formTitle: {
    fontSize: '2rem',
    fontWeight: '600',
    color: '#1a1a1a',
    margin: 0
  },
  formActions: {
    display: 'flex',
    gap: '0.75rem'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '1.5rem',
    marginBottom: '1.5rem'
  },
  formGroup: {
    marginBottom: '1.5rem'
  },
  formLabel: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#374151',
    fontSize: '0.875rem'
  },
  formInput: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    outline: 'none',
    fontFamily: 'inherit'
  },
  formInputFocus: {
    borderColor: '#3b82f6',
    boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)'
  },
  formTextarea: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease',
    outline: 'none',
    fontFamily: 'inherit',
    resize: 'vertical' as const,
    minHeight: '100px'
  },
  formSelect: {
    width: '100%',
    padding: '0.75rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'white',
    outline: 'none',
    cursor: 'pointer'
  },
  tagsContainer: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '0.5rem',
    marginTop: '0.5rem'
  },
  tagInput: {
    padding: '0.5rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '0.875rem',
    outline: 'none',
    minWidth: '100px'
  },
  tagItem: {
    backgroundColor: '#e5e7eb',
    color: '#374151',
    padding: '0.375rem 0.75rem',
    borderRadius: '20px',
    fontSize: '0.875rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem'
  },
  tagRemove: {
    background: 'none',
    border: 'none',
    color: '#6b7280',
    cursor: 'pointer',
    padding: '0',
    display: 'flex',
    alignItems: 'center'
  },
  buttonPrimary: {
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  buttonSecondary: {
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  },
  buttonSuccess: {
    backgroundColor: '#10b981',
    color: 'white',
    border: '1px solid #059669',
    padding: '0.5rem 1rem',
    borderRadius: '4px',
    fontSize: '0.875rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    minWidth: '120px',
    '&:hover': {
      backgroundColor: '#059669'
    },
    '&:disabled': {
      backgroundColor: '#9ca3af',
      borderColor: '#9ca3af',
      cursor: 'not-allowed'
    }
  },
  addPostBtn: {
    position: 'fixed' as const,
    bottom: '2rem',
    right: '2rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    padding: '1rem',
    borderRadius: '50%',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
    transition: 'all 0.3s ease',
    zIndex: 1000
  },
  errorMessage: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    color: '#991b1b'
  },
  successMessage: {
    backgroundColor: '#ecfdf5',
    border: '1px solid #a7f3d0',
    color: '#065f46'
  },
  postStats: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #eee',
    fontSize: '0.875rem',
    color: '#666'
  },
  commentsSection: {
    marginTop: '2rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #eee'
  },
  comment: {
    padding: '1rem',
    marginBottom: '1rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px'
  },
  commentAuthor: {
    fontWeight: '600',
    marginBottom: '0.5rem'
  },
  commentDate: {
    fontSize: '0.75rem',
    color: '#666',
    marginBottom: '0.5rem'
  },
  commentText: {
    lineHeight: '1.6'
  },
  messageContainer: {
    marginBottom: '1.5rem',
    padding: '1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    animation: 'slideIn 0.3s ease-out'
  },
  messageIcon: {
    flexShrink: 0,
    marginTop: '0.125rem'
  },
  messageContent: {
    flex: 1,
    fontSize: '0.875rem',
    lineHeight: '1.5'
  },
  messageTitle: {
    fontWeight: '600',
    marginBottom: '0.25rem'
  },
  messageText: {
    margin: 0
  },
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateY(-10px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }
};

const imageUploadStyles = {
  uploadContainer: {
    marginBottom: '1.5rem',
  },
  uploadLabel: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: '500',
    color: '#374151',
  },
  uploadBox: {
    border: '2px dashed #d1d5db',
    borderRadius: '0.375rem',
    padding: '1.5rem',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    backgroundColor: '#f9fafb',
    '&:hover': {
      borderColor: '#9ca3af',
      backgroundColor: '#f3f4f6',
    },
  },
  uploadBoxActive: {
    borderColor: '#3b82f6',
    backgroundColor: '#eff6ff',
  },
  uploadInput: {
    display: 'none',
  },
  uploadContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.5rem',
  },
  uploadIcon: {
    color: '#9ca3af',
  },
  uploadText: {
    fontSize: '0.875rem',
    color: '#6b7280',
  },
  uploadButton: {
    marginTop: '0.5rem',
    padding: '0.375rem 0.75rem',
    backgroundColor: '#3b82f6',
    color: 'white',
    borderRadius: '0.375rem',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.875rem',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    '&:hover': {
      backgroundColor: '#2563eb',
    },
  },
  previewContainer: {
    marginTop: '1rem',
    position: 'relative' as const,
  },
  previewImage: {
    maxWidth: '100%',
    maxHeight: '300px',
    borderRadius: '0.375rem',
    border: '1px solid #e5e7eb',
  },
  removeImage: {
    position: 'absolute' as const,
    top: '0.5rem',
    right: '0.5rem',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    color: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
    },
  },
};

const categories = ["News", "Story", 'Other'];

const NewBlog: React.FC = () => {
  const navigate = useNavigate();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState<BlogFormData>({
    title: '',
    author: '',
    category: categories[0],
    readTime: 1,
    excerpt: '',
    content: '',
    tags: []
  });

  const [tagInput, setTagInput] = useState<string>('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showPreview, setShowPreview] = useState(false);
  const user = auth.currentUser;
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImageUpload = async () => {
    if (!imageFile || !user) return null;
    
    setIsUploadingImage(true);
    try {
      const userId = user.uid;
      const imageUrl = await uploadImage(imageFile, userId);
      return imageUrl;
    } catch (error) {
      console.error('Error uploading image:', error);
      return null;
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleInputChange = (field: keyof BlogFormData, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (!formData.tags.includes(newTag)) {
        setFormData(prev => ({
          ...prev,
          tags: [...prev.tags, newTag]
        }));
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validateForm = (): FormErrors => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.author.trim()) {
      newErrors.author = 'Author is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.excerpt.trim()) {
      newErrors.excerpt = 'Excerpt is required';
    }
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    return newErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitStatus({ type: null, message: '' });

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      let imageUrl = await handleImageUpload();
      const token = user ? await getIdToken(user) : null;
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await API.post('/blogs',
        {
          ...formData,
          imageUrl: imageUrl || null,
          date: new Date().toISOString(),
          status: 'published'
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.status === 201 || response.status === 200) {
        setSubmitStatus({
          type: 'success',
          message: 'Blog post published successfully! Redirecting to stories page...'
        });

        setFormData({
          title: '',
          author: '',
          category: categories[0],
          readTime: 1,
          excerpt: '',
          content: '',
          tags: []
        });
        setTagInput('');
        setErrors({});
        setImageFile(null);
        setImagePreview(null);

        setTimeout(() => {
          navigate('/user-stories');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.message || error.message || 'Failed to publish blog post. Please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    const words = formData.content.split(/\s+/).filter(word => word.length > 0).length;
    const estimatedTime = Math.max(1, Math.ceil(words / 200));
    setFormData(prev => ({ ...prev, readTime: estimatedTime }));
  }, [formData.content]);

  const renderStatusMessage = () => {
    if (!submitStatus.type) return null;

    const isSuccess = submitStatus.type === 'success';
    const Icon = isSuccess ? CheckCircle2 : AlertCircle;
    const title = isSuccess ? 'Success!' : 'Error';

    return (
      <div
        style={{
          ...styles.messageContainer,
          ...(isSuccess ? styles.successMessage : styles.errorMessage)
        }}
      >
        <Icon
          size={20}
          style={styles.messageIcon}
          color={isSuccess ? '#059669' : '#dc2626'}
        />
        <div style={styles.messageContent}>
          <div style={styles.messageTitle}>{title}</div>
          <p style={styles.messageText}>{submitStatus.message}</p>
        </div>
      </div>
    );
  };

  if (showPreview) {
    const previewPost: BlogPost = {
      ...formData,
      id: 0,
      date: new Date().toISOString().split('T')[0],
      imageUrl: imagePreview || undefined
    };

    return (
      <div style={styles.formContainer}>
        <div style={styles.formHeader}>
          <h2 style={styles.formTitle}>Preview Post</h2>
          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.buttonSecondary}
              onClick={() => setShowPreview(false)}
            >
              <ArrowLeft size={16} />
              Back to Edit
            </button>
          </div>
        </div>
        <PostDetail post={previewPost} onBack={() => setShowPreview(false)} />
      </div>
    );
  }

  return (
    <div>
      <NavBar />

      <div style={styles.formContainer}>
        <div style={styles.formHeader}>
          <h2 style={styles.formTitle}>Create New Blog Post</h2>
          <div style={styles.formActions}>
            <button
              type="button"
              style={styles.buttonSecondary}
              onClick={() => setShowPreview(true)}
              disabled={!formData.title || !formData.content}
            >
              <Eye size={16} />
              Preview
            </button>
            <button
              type="button"
              style={styles.buttonSecondary}
              onClick={() => navigate('/user-stories')}
            >
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Title *</label>
              <input
                type="text"
                style={styles.formInput}
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter blog post title..."
              />
              {errors.title && <div style={styles.errorMessage}>{errors.title}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Author *</label>
              <input
                type="text"
                style={styles.formInput}
                value={formData.author}
                onChange={(e) => handleInputChange('author', e.target.value)}
                placeholder="Enter author name..."
              />
              {errors.author && <div style={styles.errorMessage}>{errors.author}</div>}
            </div>
          </div>

          <div style={styles.formGrid}>
            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Category *</label>
              <select
                style={styles.formSelect}
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                {categories.map(category => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              {errors.category && <div style={styles.errorMessage}>{errors.category}</div>}
            </div>

            <div style={styles.formGroup}>
              <label style={styles.formLabel}>Estimated Reading Time</label>
              <input
                type="number"
                style={styles.formInput}
                value={formData.readTime}
                onChange={(e) => handleInputChange('readTime', parseInt(e.target.value) || 1)}
                min="1"
                max="60"
              />
              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
                Auto-calculated based on content length
              </div>
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Excerpt *</label>
            <textarea
              style={styles.formTextarea}
              value={formData.excerpt}
              onChange={(e) => handleInputChange('excerpt', e.target.value)}
              placeholder="Enter a brief description of your blog post..."
              rows={3}
            />
            {errors.excerpt && <div style={styles.errorMessage}>{errors.excerpt}</div>}
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {formData.excerpt.length} characters
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Content *</label>
            <textarea
              style={styles.formTextarea}
              value={formData.content}
              onChange={(e) => handleInputChange('content', e.target.value)}
              placeholder="Write your blog post content here... You can use Markdown formatting."
              rows={15}
            />
            {errors.content && <div style={styles.errorMessage}>{errors.content}</div>}
            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {formData.content.split(/\s+/).filter(word => word.length > 0).length} words
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Tags</label>
            <input
              type="text"
              style={styles.tagInput}
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Add a tag and press Enter..."
            />
            <div style={styles.tagsContainer}>
              {formData.tags.map(tag => (
                <span key={tag} style={styles.tagItem}>
                  <Tag size={12} />
                  {tag}
                  <button
                    type="button"
                    style={styles.tagRemove}
                    onClick={() => handleRemoveTag(tag)}
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={imageUploadStyles.uploadLabel as React.CSSProperties}>Featured Image</label>
            <div 
              style={{
                ...imageUploadStyles.uploadBox as React.CSSProperties,
                ...(imagePreview ? imageUploadStyles.uploadBoxActive as React.CSSProperties : {}),
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={imageUploadStyles.uploadInput as React.CSSProperties}
                ref={fileInputRef}
              />
              <div style={imageUploadStyles.uploadContent as React.CSSProperties}>
                {imagePreview ? (
                  <div style={imageUploadStyles.previewContainer as React.CSSProperties}>
                    <img 
                      src={imagePreview} 
                      alt="Preview" 
                      style={imageUploadStyles.previewImage as React.CSSProperties}
                    />
                    <button
                      type="button"
                      style={imageUploadStyles.removeImage as React.CSSProperties}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage();
                      }}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <ImageIcon size={24} style={imageUploadStyles.uploadIcon as React.CSSProperties} />
                    <div style={imageUploadStyles.uploadText as React.CSSProperties}>
                      Drag and drop an image here, or click to select
                    </div>
                    <button 
                      type="button" 
                      style={imageUploadStyles.uploadButton as React.CSSProperties}
                      onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current?.click();
                      }}
                    >
                      <Upload size={14} />
                      Select Image
                    </button>
                  </>
                )}
              </div>
            </div>
            {isUploadingImage && (
              <div style={{ marginTop: '0.5rem', color: '#3b82f6' }}>
                Uploading image...
              </div>
            )}
          </div>

          <div style={styles.formActions}>
            <button
              type="submit"
              style={{
                ...styles.buttonSuccess,
                opacity: isSubmitting ? 0.7 : 1,
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                width: 'auto',
                margin: '0 auto',
                display: 'block'
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="spinner-border spinner-border-sm" role="status" style={{ marginRight: '0.5rem' }}>
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  Publishing...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Publish Post
                </>
              )}
            </button>
          </div>
          <div style={{ marginTop: '1rem' }}>
            {renderStatusMessage()}
          </div>
        </form>
      </div>
    </div>
  );
};

const PostDetail: React.FC<{ post: BlogPost; onBack: () => void }> = ({ post, onBack }) => {
  const [isBackHovered, setIsBackHovered] = useState(false);

  return (
    <div>
      <div style={styles.postDetail}>
        <button
          style={{
            ...styles.backBtn,
            ...(isBackHovered ? styles.backBtnHover : {})
          }}
          onClick={onBack}
          onMouseEnter={() => setIsBackHovered(true)}
          onMouseLeave={() => setIsBackHovered(false)}
        >
          <ArrowLeft size={16} />
          Back to all posts
        </button>

        {post.imageUrl && (
          <img 
            src={post.imageUrl} 
            alt={post.title} 
            style={{ 
              width: '100%', 
              maxHeight: '400px', 
              objectFit: 'cover', 
              borderRadius: '8px',
              marginBottom: '2rem'
            }} 
          />
        )}

        <h1 style={styles.postDetailTitle}>{post.title}</h1>

        <div style={styles.postDetailMeta}>
          <div style={styles.postMetaItem}>
            <User size={18} />
            <span>{post.author}</span>
          </div>
          <div style={styles.postMetaItem}>
            <Calendar size={18} />
            <span>{new Date(post.date).toLocaleDateString()}</span>
          </div>
          <div style={styles.postMetaItem}>
            <span>{post.readTime} min read</span>
          </div>
          <div style={styles.postCategory}>{post.category}</div>
        </div>

        <div style={styles.postTags}>
          {post.tags.map(tag => (
            <span key={tag} style={styles.tag}>
              <Tag size={12} />
              {tag}
            </span>
          ))}
        </div>

        <div style={styles.postContent}>
          {post.content.split('\n').map((paragraph, index) => {
            if (paragraph.startsWith('## ')) {
              return <h2 key={index} style={{ marginTop: '2rem', marginBottom: '1rem', fontSize: '1.5rem', fontWeight: '600' }}>{paragraph.replace('## ', '')}</h2>;
            }
            if (paragraph.startsWith('### ')) {
              return <h3 key={index} style={{ marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.25rem', fontWeight: '600' }}>{paragraph.replace('### ', '')}</h3>;
            }
            if (paragraph.startsWith('```')) {
              return <pre key={index} style={{ backgroundColor: '#f8f9fa', padding: '1rem', borderRadius: '8px', overflowX: 'auto', margin: '1rem 0' }}><code>{paragraph.replace(/```\w*\n?/, '').replace(/```$/, '')}</code></pre>;
            }
            if (paragraph.startsWith('- **')) {
              return <li key={index} style={{ marginBottom: '0.5rem' }}>{paragraph.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')}</li>;
            }
            if (paragraph.trim()) {
              return <p key={index} style={{ marginBottom: '1rem' }}>{paragraph}</p>;
            }
            return null;
          })}
        </div>

        {(post.views || post.likes || post.rating) && (
          <div style={styles.postStats}>
            {post.views && <span>Views: {post.views}</span>}
            {post.rating && <span>Rating: {'★'.repeat(post.rating)}</span>}
            {post.likes && <span>Likes: {post.likes}</span>}
            {post.dislikes && <span>Dislikes: {post.dislikes}</span>}
          </div>
        )}

        {post.comments && post.comments.length > 0 && (
          <div style={styles.commentsSection}>
            <h3 style={{ marginBottom: '1rem' }}>Comments ({post.comments.length})</h3>
            {post.comments.map(comment => (
              <div key={comment.id} style={styles.comment}>
                <div style={styles.commentAuthor}>{comment.author}</div>
                <div style={styles.commentDate}>{new Date(comment.date).toLocaleDateString()}</div>
                <div style={styles.commentText}>{comment.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NewBlog;